import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { Button } from '@/components/ui/button';
import { useNamespaceCopy, formatCopy } from '@/i18n/ns';
import approvalsCopy from '@/i18n/ns/app-approvals';
import lifecycleCopy from '@/i18n/ns/app-lifecycle';
import { AppDialog } from '../lifecycle/app-dialog';

export type NativeWriteReview = {
  operation: 'files.write' | 'collections.undo';
  reviewDigest: string;
  summary:
    | {
        fileId: string;
        mimeType: string;
        byteLength: number;
        expectedVersion: string | null;
        sha256: string;
      }
    | {
        changes: Array<{
          key: { collectionId: string; rowId: string };
          before: unknown;
          after: unknown;
        }>;
      };
};

/** Renders only the server's current bounded review. App labels/flags cannot
 * select an approval; the actual button is owned by the surrounding host. */
export function NativeWriteDialog({
  title,
  review,
  busy,
  failed,
  decide,
}: {
  title: string;
  review: NativeWriteReview;
  busy: boolean;
  failed: boolean;
  decide(approved: boolean): void;
}) {
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(approvalsCopy);
  const lifecycle = useNamespaceCopy(lifecycleCopy);
  const summary = review.summary;
  return (
    <AppDialog
      title={formatCopy(copy.writeTitle, { target: title })}
      closeLabel={lifecycle.close}
      busy={busy}
      onClose={() => decide(false)}
      testId="native-app-write-confirmation"
      footer={
        <>
          <Button variant="outline" disabled={busy} onClick={() => decide(false)}>
            {lifecycle.cancel}
          </Button>
          <Button
            disabled={busy || failed}
            onClick={() => decide(true)}
            data-testid="native-app-write-approve"
          >
            {busy ? copy.working : copy.approve}
          </Button>
        </>
      }
    >
      {'changes' in summary ? (
        <ul>
          {summary.changes.map((row) => (
            <li key={`${row.key.collectionId}:${row.key.rowId}`}>
              <strong>
                {row.key.collectionId} · {row.key.rowId}
              </strong>
              <dl>
                <dt>{copy.fieldBefore}</dt>
                <dd>
                  <pre>{JSON.stringify(row.before, null, 2)}</pre>
                </dd>
                <dt>{copy.fieldAfter}</dt>
                <dd>
                  <pre>{JSON.stringify(row.after, null, 2)}</pre>
                </dd>
              </dl>
            </li>
          ))}
        </ul>
      ) : (
        <dl>
          <dt>{summary.fileId}</dt>
          <dd>
            {summary.mimeType} · {summary.byteLength.toLocaleString(locale)} B
          </dd>
          <dt>{copy.fieldBefore}</dt>
          <dd>{summary.expectedVersion ?? copy.fieldEmpty}</dd>
          <dt>{copy.fieldAfter}</dt>
          <dd>
            <code>{summary.sha256}</code>
          </dd>
        </dl>
      )}
      {failed && <p role="alert">{copy.cannotDecideNow}</p>}
    </AppDialog>
  );
}
